import { Field, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectConfigurationParameterById } from '!core/redux';
import { TrlTextWithLink } from '»components/atoms';
import { TrlCheckboxField } from '»components/molecules/TrlCheckboxField';
import { TrlFormButtons } from '»components/molecules/TrlFormButtons';
import { isStinvestActiveClient } from '»utils/clientsUtils';
import { ANGOLAN_IBAN_PREFIX } from '»utils/constants';
import { isSubmitFormButtonDisabled } from '»utils/form';

import { FormStyled } from '../Onboarding.styles';
import { onboardingServices, onboardingSlice } from '../redux';
import { onboardingCreateAccountDisclaimerSelector } from '../redux/onboarding.selectors';
import { CREATE_ACCOUNT_VARIANTS } from './CreateAccount.helper';
import { DataAccessAgreementControl } from './CreateAccount.styles';

export const CreateAccount = () => {
  const dispatch = useDispatch();

  const formRef = useRef(null);

  const [bankInfo, setBankInfo] = useState([]);

  const mustAllowDataProcessing =
    useSelector((state) =>
      selectConfigurationParameterById(state, 'ONBOARDING_DATA_ACCEPT'),
    ).value === 'Y';
  const onboardingVideoLink = useSelector((state) =>
    selectConfigurationParameterById(state, 'ONBOARDING_VIDEO_LINK'),
  )?.value;
  const acceptUsePersonalDataLabel = useSelector(
    onboardingCreateAccountDisclaimerSelector,
  );
  const hasOnboardingInitialOtp =
    useSelector((state) =>
      selectConfigurationParameterById(state, 'ONBOARDING_OTP_START'),
    )?.value === 'Y';

  const formVariant =
    CREATE_ACCOUNT_VARIANTS[isStinvestActiveClient ? 'stinvest' : 'default'];
  const { initialValues, buildValidationSchema, FieldsComponent } = formVariant;

  const validationSchema = buildValidationSchema({
    mustAllowDataProcessing,
  });

  const safeOnboardingVideoUrl = onboardingVideoLink
    ? onboardingVideoLink.startsWith('http://') ||
      onboardingVideoLink.startsWith('https://')
      ? onboardingVideoLink.trim()
      : `https://${onboardingVideoLink.trim()}`
    : '';

  useEffect(() => {
    onboardingServices.getBankInfo().then((resp) => {
      const formattedBankInfo = resp.reduce((acc, bank) => {
        acc.push({
          label: bank.descr,
          value: bank.bankCode,
          bankIbanCode: bank.bankCodigoIban,
        });

        return acc;
      }, []);

      if (formattedBankInfo.length === 1) {
        formRef.current?.setFieldValue('bankCode', formattedBankInfo[0].value);

        if (
          formattedBankInfo[0].bankIbanCode &&
          formRef.current?.values.ibanCode === ANGOLAN_IBAN_PREFIX
        )
          formRef.current.setFieldValue(
            'ibanCode',
            formRef.current.values.ibanCode + formattedBankInfo[0].bankIbanCode,
          );
      }

      setBankInfo(formattedBankInfo);
    });
  }, []);

  const handleSubmit = (values, { setSubmitting }) => {
    const disableFormSubmittingState = () => {
      setSubmitting(false);
    };

    dispatch(
      onboardingSlice.actions.getNewAccountRequest({
        ...values,
        hasOnboardingInitialOtp,
        disableFormSubmittingState,
      }),
    );
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={{
        ...initialValues,
        dataAccessAgreement: 'F',
      }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ isSubmitting, dirty, errors, values, setFieldValue }) => (
        <FormStyled className="formContainer" hasMarginBottom>
          <FieldsComponent bankInfo={bankInfo} />

          {mustAllowDataProcessing && (
            <Field name="dataAccessAgreement">
              {() => (
                <DataAccessAgreementControl>
                  <TrlCheckboxField
                    checked={values.dataAccessAgreement}
                    handleCheckboxChange={(event) => {
                      setFieldValue(
                        'dataAccessAgreement',
                        event.target.checked ? 'Y' : 'N',
                      );
                    }}
                    isCheckboxDisabled={!acceptUsePersonalDataLabel}
                    checkboxText={acceptUsePersonalDataLabel}
                    hasHtmlTagsInCheckboxText
                  />
                </DataAccessAgreementControl>
              )}
            </Field>
          )}

          {onboardingVideoLink && (
            <TrlTextWithLink
              mainText="TRLT_UI-NEED_HELP"
              linkText="TRLT_UI-SUPPORT_VIDEO"
              href={safeOnboardingVideoUrl}
            />
          )}

          <TrlFormButtons
            advanceButtonName="TRLT_UI-NEXT"
            isAdvanceButtonDisabled={isSubmitFormButtonDisabled({
              isSubmitting,
              dirty,
              errors,
              values,
              areFiles: false,
            })}
          />
        </FormStyled>
      )}
    </Formik>
  );
};
